import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Bell, Calendar, Download, Search, Filter, RefreshCw,
  Building2, User, CheckCircle, Clock, AlertTriangle,
  FileText, Loader2, MoreVertical, Check, X, Trash2,
  DollarSign, ChevronLeft, ChevronRight, MessageCircle, Send,
  TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { dueNoticesAPI, propertiesAPI, rentPaymentsAPI, transactionsAPI, clientsAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const STATUS_CONFIG = {
  en_attente: { label: 'Non paye', badgeClass: 'badge-status badge-late', icon: Clock },
  paye: { label: 'Paye', badgeClass: 'badge-status badge-paid', icon: CheckCircle },
  en_retard: { label: 'En retard', badgeClass: 'badge-status badge-unpaid', icon: AlertTriangle },
  partiellement_paye: { label: 'Partiel', badgeClass: 'badge-status badge-partial', icon: DollarSign },
};

const MONTH_NAMES = [
  'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'
];

const DueNotices = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin' || user?.is_super_admin === true;
  const [notices, setNotices] = useState([]);
  const [properties, setProperties] = useState([]);
  const [rentPayments, setRentPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  // Individual creation modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeLeases, setActiveLeases] = useState([]);
  const [createForm, setCreateForm] = useState({
    transaction_id: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    amount: '',
    description: '',
  });

  // Partial payment modal
  const [showPartialModal, setShowPartialModal] = useState(false);
  const [partialNotice, setPartialNotice] = useState(null);
  const [partialAmount, setPartialAmount] = useState('');
  const [partialNotes, setPartialNotes] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProperty, setFilterProperty] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [noticesRes, propertiesRes, paymentsRes] = await Promise.all([
        dueNoticesAPI.getAll(),
        propertiesAPI.getAll({ transaction_type: 'location' }),
        rentPaymentsAPI.getAll({ status: 'confirme' }).catch(() => ({ data: [] })),
      ]);
      setNotices(noticesRes.data);
      setProperties(propertiesRes.data);
      setRentPayments(paymentsRes.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des avis');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreateModal = async () => {
    // Fetch active rental transactions to populate the tenant list
    try {
      const r = await transactionsAPI.getAll({ type: 'location' });
      const list = (r.data || []).filter(t => (t.lease_status || 'actif') === 'actif');
      // Resolve tenant + property display names
      const clientsCache = {};
      const propsCache = {};
      const enriched = await Promise.all(list.map(async (t) => {
        let tenantName = t.client_name;
        let propTitle = t.property_title;
        if (!tenantName && t.client_id) {
          if (!clientsCache[t.client_id]) {
            try {
              const cr = await clientsAPI.getById(t.client_id);
              clientsCache[t.client_id] = cr.data;
            } catch { clientsCache[t.client_id] = {}; }
          }
          tenantName = clientsCache[t.client_id]?.name;
        }
        if (!propTitle && t.property_id) {
          if (!propsCache[t.property_id]) {
            try {
              const pr = await propertiesAPI.getById(t.property_id);
              propsCache[t.property_id] = pr.data;
            } catch { propsCache[t.property_id] = {}; }
          }
          propTitle = propsCache[t.property_id]?.title;
        }
        return { ...t, _tenant_name: tenantName || 'Locataire', _property_title: propTitle || 'Bien' };
      }));
      setActiveLeases(enriched);
    } catch (e) {
      setActiveLeases([]);
      toast.error('Impossible de charger les baux actifs');
    }
    setCreateForm({
      transaction_id: '',
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      amount: '',
      description: '',
    });
    setShowCreateModal(true);
  };

  // Auto-fill amount when tenant + period change (uses backend prorata)
  useEffect(() => {
    if (!showCreateModal || !createForm.transaction_id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await rentPaymentsAPI.getExpectedAmount({
          transaction_id: createForm.transaction_id,
          year: createForm.year,
          month: createForm.month,
        });
        if (cancelled) return;
        const expected = r.data?.expected_amount;
        if (expected != null) {
          setCreateForm(prev => ({ ...prev, amount: String(expected) }));
        }
      } catch (e) {
        // silent
      }
    })();
    return () => { cancelled = true; };
  }, [showCreateModal, createForm.transaction_id, createForm.year, createForm.month]);

  const handleCreateNotice = async (keepOpen = false) => {
    if (!createForm.transaction_id) {
      toast.error('Veuillez sélectionner un locataire');
      return;
    }
    const amount = parseFloat(createForm.amount);
    if (!amount || amount <= 0) {
      toast.error('Veuillez saisir un montant valide');
      return;
    }
    const lease = activeLeases.find(l => l.id === createForm.transaction_id);
    if (!lease) {
      toast.error('Bail introuvable');
      return;
    }
    setCreating(true);
    try {
      const { year, month } = createForm;
      const lastDay = new Date(year, month, 0).getDate();
      const periodStart = new Date(Date.UTC(year, month - 1, 1)).toISOString();
      const periodEnd = new Date(Date.UTC(year, month - 1, lastDay, 23, 59, 59)).toISOString();
      const dueDate = new Date(Date.UTC(year, month - 1, 5)).toISOString();
      const monthName = MONTH_NAMES[month - 1];
      await dueNoticesAPI.create({
        property_id: lease.property_id,
        tenant_id: lease.client_id,
        amount,
        currency: lease.currency || 'XOF',
        due_date: dueDate,
        period_start: periodStart,
        period_end: periodEnd,
        description: createForm.description || `Loyer du mois de ${monthName} ${year}`,
      });
      toast.success(`Avis créé pour ${lease._tenant_name} — ${monthName} ${year}`);
      if (keepOpen) {
        // Reset tenant + amount + description, keep year/month
        setCreateForm(prev => ({
          ...prev,
          transaction_id: '',
          amount: '',
          description: '',
        }));
      } else {
        setShowCreateModal(false);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadPdf = async (noticeId, noticeNumber) => {
    setDownloadingPdf(noticeId);
    try {
      const response = await dueNoticesAPI.downloadPdf(noticeId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `avis_echeance_${noticeNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF telecharge');
    } catch (error) {
      toast.error('Erreur lors du telechargement');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleUpdateStatus = async (noticeId, newStatus, extraData = {}) => {
    setUpdatingStatus(noticeId);
    try {
      await dueNoticesAPI.update(noticeId, { status: newStatus, ...extraData });
      setNotices(prev => prev.map(n =>
        n.id === noticeId ? { ...n, status: newStatus, ...extraData } : n
      ));
      const label = STATUS_CONFIG[newStatus]?.label || newStatus;
      toast.success(`Statut mis a jour : ${label}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handlePartialPayment = async () => {
    if (!partialNotice || !partialAmount) return;
    setUpdatingStatus(partialNotice.id);
    try {
      await dueNoticesAPI.update(partialNotice.id, {
        status: 'partiellement_paye',
        amount_paid: parseFloat(partialAmount),
        payment_notes: partialNotes
      });
      setNotices(prev => prev.map(n =>
        n.id === partialNotice.id ? { ...n, status: 'partiellement_paye', amount_paid: parseFloat(partialAmount) } : n
      ));
      toast.success('Paiement partiel enregistre');
      setShowPartialModal(false);
      setPartialNotice(null);
      setPartialAmount('');
      setPartialNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDeleteNotice = async (noticeId, noticeNumber) => {
    if (!window.confirm(`Supprimer l'avis ${noticeNumber} ?`)) return;
    setUpdatingStatus(noticeId);
    try {
      await dueNoticesAPI.delete(noticeId);
      setNotices(prev => prev.filter(n => n.id !== noticeId));
      toast.success(`Avis ${noticeNumber} supprime`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleMarkPaid = async (notice) => {
    const msg = `Marquer l'avis ${notice.notice_number} comme paye ?\n\n` +
      `Cela va automatiquement creer une quittance pour ${notice.tenant_name} ` +
      `d'un montant de ${notice.amount} ${notice.currency || 'XOF'}.`;
    if (!window.confirm(msg)) return;
    setUpdatingStatus(notice.id);
    try {
      const res = await dueNoticesAPI.markPaid(notice.id);
      setNotices(prev => prev.map(n => n.id === notice.id
        ? { ...n, status: 'paye', rent_payment_id: res.data.rent_payment_id, receipt_number: res.data.receipt_number, paid_at: new Date().toISOString() }
        : n
      ));
      toast.success(`Quittance ${res.data.receipt_number || 'genere'} - avis marque comme paye`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du marquage');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return null;
    let clean = phone.replace(/[\s\-\(\)]/g, '');
    if (clean.startsWith('+')) return clean;
    if (clean.startsWith('00')) return '+' + clean.substring(2);
    if (clean.length === 8) return '+223' + clean;
    return '+223' + clean;
  };

  const buildWhatsAppMessage = (notice) => {
    const period = formatPeriod(notice.period_start);
    const due = notice.due_date ? new Date(notice.due_date).toLocaleDateString('fr-FR') : '';
    const statusLabel = STATUS_CONFIG[notice.status]?.label || 'Non paye';
    let msg = `Bonjour ${notice.tenant_name || ''},\n\n`;
    msg += `Un avis d'echeance est disponible dans votre espace locataire :\n\n`;
    msg += `Bien : ${notice.property_title || ''}\n`;
    msg += `Periode : ${period}\n`;
    msg += `Montant : ${formatCurrency(notice.amount, notice.currency)}\n`;
    msg += `Echeance : ${due}\n`;
    msg += `Statut : ${statusLabel}\n`;
    if (notice.status === 'partiellement_paye' && notice.amount_paid > 0) {
      msg += `Deja paye : ${formatCurrency(notice.amount_paid, notice.currency)}\n`;
      msg += `Reste : ${formatCurrency(notice.amount - notice.amount_paid, notice.currency)}\n`;
    }
    msg += `\nRef: ${notice.notice_number}\n`;
    msg += `\nMerci de regulariser votre situation.\nGAB'S SERVICES HABITAT`;
    return msg;
  };

  const sendWhatsApp = (notice) => {
    const phone = formatPhone(notice.tenant_phone);
    if (!phone) {
      toast.error(`Pas de numero de telephone pour ${notice.tenant_name || 'ce locataire'}`);
      return;
    }
    const msg = buildWhatsAppMessage(notice);
    const url = `https://wa.me/${phone.replace('+', '')}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
    toast.success(`WhatsApp ouvert pour ${notice.tenant_name}`);
  };

  const sendWhatsAppBulk = () => {
    const unpaid = filteredNotices.filter(n => n.status !== 'paye' && n.tenant_phone);
    if (unpaid.length === 0) {
      toast.error('Aucun locataire a notifier (tous payes ou pas de telephone)');
      return;
    }
    // Open first one, show count
    sendWhatsApp(unpaid[0]);
    if (unpaid.length > 1) {
      toast.info(`${unpaid.length} locataires a notifier. Envoyez un par un via le bouton WhatsApp de chaque avis.`);
    }
  };

  const availableMonths = useMemo(() => {
    const months = new Set();
    notices.forEach(notice => {
      if (notice.period_start) {
        months.add(notice.period_start.substring(0, 7));
      }
    });
    return Array.from(months).sort().reverse();
  }, [notices]);

  const filteredNotices = useMemo(() => {
    return notices.filter(notice => {
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        if (!notice.tenant_name?.toLowerCase().includes(s) &&
            !notice.property_title?.toLowerCase().includes(s) &&
            !notice.notice_number?.toLowerCase().includes(s)) return false;
      }
      if (filterProperty !== 'all' && notice.property_id !== filterProperty) return false;
      if (filterStatus !== 'all' && notice.status !== filterStatus) return false;
      if (filterMonth !== 'all' && notice.period_start?.substring(0, 7) !== filterMonth) return false;
      return true;
    });
  }, [notices, searchTerm, filterProperty, filterStatus, filterMonth]);

  const stats = useMemo(() => {
    const total = filteredNotices.length;
    const pending = filteredNotices.filter(n => n.status === 'en_attente').length;
    const paid = filteredNotices.filter(n => n.status === 'paye').length;
    const overdue = filteredNotices.filter(n => n.status === 'en_retard').length;
    const partial = filteredNotices.filter(n => n.status === 'partiellement_paye').length;
    const totalAmount = filteredNotices.reduce((sum, n) => sum + (n.amount || 0), 0);

    // Taux de paiement base sur les paiements de loyer reels (pas sur le statut des avis)
    // Match par (tenant_id, property_id, mois de period_start)
    const filteredMonths = new Set(
      filteredNotices
        .map(n => n.period_start?.substring(0, 7))
        .filter(Boolean)
    );
    const filteredPropIds = filterProperty !== 'all' ? new Set([filterProperty]) : null;

    let collectedAmount = 0;
    for (const p of rentPayments) {
      if (p.status !== 'confirme') continue;
      const month = (p.period_start || '').substring(0, 7);
      if (filteredMonths.size > 0 && !filteredMonths.has(month)) continue;
      if (filteredPropIds && !filteredPropIds.has(p.property_id)) continue;
      collectedAmount += (p.amount || 0);
    }
    const paymentRatePct = totalAmount > 0
      ? Math.round((collectedAmount / totalAmount) * 1000) / 10
      : 0;

    return { total, pending, paid, overdue, partial, totalAmount, collectedAmount, paymentRatePct };
  }, [filteredNotices, rentPayments, filterProperty]);

  const getStatusBadge = (notice) => {
    const cfg = STATUS_CONFIG[notice.status] || STATUS_CONFIG.en_attente;
    // Auto-detect overdue for en_attente
    if (notice.status === 'en_attente') {
      const dueDate = new Date(notice.due_date);
      if (dueDate < new Date()) {
        return (
          <span className="badge-status badge-unpaid" data-testid={`status-${notice.id}`}>
            <AlertTriangle className="w-3 h-3" />
            Non paye (en retard)
          </span>
        );
      }
    }
    const Icon = cfg.icon;
    return (
      <span className={cfg.badgeClass} data-testid={`status-${notice.id}`}>
        {Icon ? <Icon className="w-3 h-3" /> : null}
        {cfg.label}
      </span>
    );
  };

  const formatPeriod = (start) => {
    if (!start) return 'N/A';
    try {
      const d = new Date(start);
      return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    } catch { return start; }
  };

  return (
    <div className="space-y-6" data-testid="due-notices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
            Avis d&apos;Echeance
          </h1>
          <p className="text-gray-500 mt-1">
            Generez, suivez et confirmez les paiements des locataires
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={sendWhatsAppBulk}
            variant="outline"
            className="border-green-600 text-green-600 hover:bg-green-50"
            data-testid="notify-whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Notifier WhatsApp
          </Button>
          {isSuperAdmin && (
            <Button
              onClick={openCreateModal}
              className="bg-[#B85C38] hover:bg-[#8F4426]"
              title="Cas exceptionnel : créer un avis manuel (Super-Admin)"
              data-testid="create-notice-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Nouvel avis (exception)
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="col-span-2 md:col-span-2 kpi-premium rise-in" data-testid="payment-rate-card">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg shrink-0 shadow-inner" style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #FEF3C7 100%)' }}>
                <TrendingUp className="w-4 h-4 text-emerald-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 font-semibold">Taux de paiement <span className="text-gray-300">·</span> <span className="text-gray-400 font-normal">basé sur les paiements de loyer</span></p>
                <p className="text-2xl font-extrabold tabular-nums text-gradient-gold" style={{ lineHeight: 1.1 }} data-testid="payment-rate-percentage">
                  {stats.paymentRatePct}%
                </p>
                <p className="text-[11px] text-gray-500 truncate">
                  {formatCurrency(stats.collectedAmount, 'XOF')} <span className="text-gray-300">/</span> {formatCurrency(stats.totalAmount, 'XOF')}
                </p>
                <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden relative">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${Math.min(stats.paymentRatePct, 100)}%`,
                      background: 'linear-gradient(90deg, #10B981 0%, #D4A745 60%, #F2A900 100%)',
                    }}
                  />
                  <div className="absolute inset-0 progress-shimmer rounded-full" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><Bell className="w-4 h-4 text-blue-600" /></div>
              <div>
                <p className="text-xs text-gray-500">Total</p>
                <p className="text-xl font-semibold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg"><Clock className="w-4 h-4 text-orange-600" /></div>
              <div>
                <p className="text-xs text-gray-500">Non payes</p>
                <p className="text-xl font-semibold text-orange-600">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg"><AlertTriangle className="w-4 h-4 text-red-600" /></div>
              <div>
                <p className="text-xs text-gray-500">En retard</p>
                <p className="text-xl font-semibold text-red-600">{stats.overdue}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><DollarSign className="w-4 h-4 text-blue-600" /></div>
              <div>
                <p className="text-xs text-gray-500">Partiels</p>
                <p className="text-xl font-semibold text-blue-600">{stats.partial}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg"><CheckCircle className="w-4 h-4 text-green-600" /></div>
              <div>
                <p className="text-xs text-gray-500">Payes</p>
                <p className="text-xl font-semibold text-green-600">{stats.paid}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Rechercher..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-input" />
            </div>
            <Select value={filterProperty} onValueChange={setFilterProperty}>
              <SelectTrigger><Building2 className="w-4 h-4 mr-2 text-gray-400" /><SelectValue placeholder="Tous les biens" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les biens</SelectItem>
                {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger><Filter className="w-4 h-4 mr-2 text-gray-400" /><SelectValue placeholder="Tous les statuts" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="en_attente">Non paye</SelectItem>
                <SelectItem value="en_retard">En retard</SelectItem>
                <SelectItem value="partiellement_paye">Partiellement paye</SelectItem>
                <SelectItem value="paye">Paye</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger><Calendar className="w-4 h-4 mr-2 text-gray-400" /><SelectValue placeholder="Tous les mois" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les mois</SelectItem>
                {availableMonths.map(m => (
                  <SelectItem key={m} value={m}>{new Date(m + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Notices Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Liste des Avis ({filteredNotices.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-[#1B3A4B]" /></div>
          ) : filteredNotices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Aucun avis d&apos;echeance trouve</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 text-sm font-medium text-gray-600">N Avis</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Locataire</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Bien</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Periode</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Echeance</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">Montant</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Statut</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNotices.map((notice) => (
                    <tr key={notice.id} className="row-premium border-b" data-testid={`notice-row-${notice.id}`}>
                      <td className="p-3 font-mono text-sm text-[#1B3A4B]">
                        {notice.notice_number}
                        {notice.auto_generated && <Badge variant="outline" className="ml-2 text-xs">Auto</Badge>}
                        {notice.is_exceptional && <Badge className="ml-2 text-xs bg-[#B85C38] text-white">Exception</Badge>}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{notice.tenant_name || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span>{notice.property_title || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="p-3 text-sm text-gray-600">{formatPeriod(notice.period_start)}</td>
                      <td className="p-3 text-sm">{formatDate(notice.due_date)}</td>
                      <td className="p-3 text-right">
                        <div>
                          <span className="font-semibold">{formatCurrency(notice.amount, notice.currency)}</span>
                          {notice.status === 'partiellement_paye' && notice.amount_paid > 0 && (
                            <p className="text-xs text-blue-600 mt-0.5">
                              Paye: {formatCurrency(notice.amount_paid, notice.currency)}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-center">{getStatusBadge(notice)}</td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(notice.id, notice.notice_number)}
                            disabled={downloadingPdf === notice.id} className="border-[#1B3A4B] text-[#1B3A4B] hover:bg-[#1B3A4B]/10"
                            data-testid={`download-pdf-${notice.id}`}>
                            {downloadingPdf === notice.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <><Download className="w-3 h-3 mr-1" />PDF</>}
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" disabled={updatingStatus === notice.id}
                                data-testid={`status-menu-${notice.id}`}>
                                {updatingStatus === notice.id
                                  ? <Loader2 className="w-4 h-4 animate-spin" />
                                  : <MoreVertical className="w-4 h-4" />}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {notice.tenant_phone && (
                                <>
                                <DropdownMenuItem onClick={() => sendWhatsApp(notice)} className="text-green-600">
                                  <MessageCircle className="w-4 h-4 mr-2" /> Notifier WhatsApp
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                </>
                              )}
                              <DropdownMenuItem onClick={() => handleMarkPaid(notice)}
                                disabled={notice.status === 'paye'} className="text-green-700 font-medium">
                                <CheckCircle className="w-4 h-4 mr-2" /> Marquer paye + Generer quittance
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleUpdateStatus(notice.id, 'paye')}
                                disabled={notice.status === 'paye'} className="text-green-600">
                                <CheckCircle className="w-4 h-4 mr-2" /> Confirmer Paye (sans quittance)
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                setPartialNotice(notice);
                                setPartialAmount(notice.amount_paid > 0 ? String(notice.amount_paid) : '');
                                setShowPartialModal(true);
                              }} className="text-blue-600">
                                <DollarSign className="w-4 h-4 mr-2" /> Paiement Partiel
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleUpdateStatus(notice.id, 'en_retard')}
                                disabled={notice.status === 'en_retard'} className="text-red-600">
                                <AlertTriangle className="w-4 h-4 mr-2" /> Marquer En retard
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleUpdateStatus(notice.id, 'en_attente')}
                                disabled={notice.status === 'en_attente'} className="text-orange-600">
                                <Clock className="w-4 h-4 mr-2" /> Remettre Non paye
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleDeleteNotice(notice.id, notice.notice_number)}
                                className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" /> Supprimer l&apos;avis
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Individual creation modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[520px]" data-testid="create-notice-modal">
          <DialogHeader>
            <DialogTitle>Nouvel avis d&apos;échéance</DialogTitle>
            <DialogDescription>
              La génération automatique est désactivée. Créez ici un avis à la fois pour un locataire précis.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Locataire (bail actif) *</Label>
              <Select
                value={createForm.transaction_id}
                onValueChange={(v) => setCreateForm(f => ({ ...f, transaction_id: v }))}
              >
                <SelectTrigger data-testid="select-lease">
                  <SelectValue placeholder="Sélectionner un locataire" />
                </SelectTrigger>
                <SelectContent>
                  {activeLeases.length === 0 && (
                    <SelectItem value="_none" disabled>Aucun bail actif</SelectItem>
                  )}
                  {activeLeases.map(l => (
                    <SelectItem key={l.id} value={l.id}>
                      {l._tenant_name} — {l._property_title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Année *</Label>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" onClick={() => setCreateForm(f => ({ ...f, year: f.year - 1 }))}>
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </Button>
                  <span className="flex-1 text-center font-semibold" data-testid="year-display">{createForm.year}</span>
                  <Button variant="outline" size="sm" onClick={() => setCreateForm(f => ({ ...f, year: f.year + 1 }))}>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div>
                <Label>Mois *</Label>
                <Select
                  value={String(createForm.month)}
                  onValueChange={(v) => setCreateForm(f => ({ ...f, month: parseInt(v, 10) }))}
                >
                  <SelectTrigger data-testid="select-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTH_NAMES.map((n, i) => (
                      <SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Montant (FCFA) *</Label>
              <Input
                type="number"
                min="0"
                step="500"
                value={createForm.amount}
                onChange={(e) => setCreateForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="Calculé automatiquement (prorata inclus)"
                data-testid="input-amount"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Auto-rempli avec le loyer + charges + prorata (si bail démarré en cours de mois).
              </p>
            </div>

            <div>
              <Label>Description (optionnel)</Label>
              <Input
                value={createForm.description}
                onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
                placeholder={`Loyer du mois de ${MONTH_NAMES[createForm.month - 1]} ${createForm.year}`}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
            <Button
              variant="outline"
              onClick={() => handleCreateNotice(true)}
              disabled={creating || !createForm.transaction_id}
              className="border-[#D4A745] text-[#8B6914] hover:bg-amber-50"
              title="Créer cet avis et garder la période pour le prochain locataire"
              data-testid="create-and-next-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ChevronRight className="w-4 h-4 mr-2" />}
              Créer &amp; suivant
            </Button>
            <Button
              onClick={() => handleCreateNotice(false)}
              disabled={creating || !createForm.transaction_id}
              className="bg-[#1B3A4B] hover:bg-[#142D3A]"
              data-testid="confirm-create-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Créer l&apos;avis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partial Payment Modal */}
      <Dialog open={showPartialModal} onOpenChange={(open) => { setShowPartialModal(open); if (!open) setPartialNotice(null); }}>
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader>
            <DialogTitle>Paiement partiel</DialogTitle>
            <DialogDescription>
              {partialNotice && `${partialNotice.tenant_name} - ${formatCurrency(partialNotice.amount, partialNotice.currency)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <div>
              <Label>Montant paye</Label>
              <Input type="number" value={partialAmount}
                onChange={e => setPartialAmount(e.target.value)}
                placeholder="Montant recu"
                data-testid="partial-amount-input" />
              {partialNotice && partialAmount && (
                <p className="text-xs text-gray-500 mt-1">
                  Reste : {formatCurrency(partialNotice.amount - parseFloat(partialAmount || 0), partialNotice.currency)}
                </p>
              )}
            </div>
            <div>
              <Label>Notes (optionnel)</Label>
              <Input value={partialNotes} onChange={e => setPartialNotes(e.target.value)}
                placeholder="Ex: 1ere tranche" data-testid="partial-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPartialModal(false)}>Annuler</Button>
            <Button onClick={handlePartialPayment} disabled={!partialAmount || updatingStatus}
              className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-partial-btn">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DueNotices;
